// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { SearchRequest } from '@medplum/core';
import { formatSearchQuery } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import type { ThreadBucket } from '@polarissleep/messaging';
import { normalizeCommunicationSearch } from '@polarissleep/messaging';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ThreadInbox } from '../../components/messages/ThreadInbox';
import classes from './MessagesPage.module.css';
/**
 * Fetches
 * @returns A React component that displays all Threads/Topics.
 */
export function MessagesPage(): JSX.Element {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const currentSearch = useMemo(() => (location.search ? location.search.substring(1) : ''), [location.search]);

  const { normalizedSearch, parsedSearch, bucket } = useMemo(
    () =>
      normalizeCommunicationSearch({
        search: currentSearch,
      }),
    [currentSearch]
  );

  useEffect(() => {
    const isDetailView = Boolean(messageId);
    if (!isDetailView && normalizedSearch !== currentSearch) {
      const prefix = normalizedSearch ? `?${normalizedSearch}` : '';
      navigate(`/Communication${prefix}`, { replace: true })?.catch(console.error);
    }
  }, [currentSearch, navigate, normalizedSearch, messageId]);

  const onChange = (search: SearchRequest): void => {
    const fhirQuery = formatSearchQuery(search);
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    navigate(`/Communication${fhirQuery}${sep}bucket=${bucket}`)?.catch(console.error);
  };

  const getThreadUri = (topic: Communication): string => {
    const fhirQuery = formatSearchQuery(parsedSearch);
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    return `/Communication/${topic.id}${fhirQuery}${sep}bucket=${bucket}`;
  };

  const buildBucketUri = (value: ThreadBucket): string => {
    const fhirQuery = formatSearchQuery({ ...parsedSearch, offset: 0 });
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    return `/Communication${fhirQuery}${sep}bucket=${value}`;
  };

  const unreadUri = buildBucketUri('unread');
  const sentUri = buildBucketUri('sent');
  const allUri = buildBucketUri('all');

  const onNew = (message: Communication): void => {
    navigate(getThreadUri(message))?.catch(console.error);
  };

  return (
    <div className={classes.container}>
      <ThreadInbox
        threadId={messageId}
        query={formatSearchQuery(parsedSearch).substring(1)}
        bucket={bucket}
        onNew={onNew}
        getThreadUri={getThreadUri}
        onChange={onChange}
        unreadUri={unreadUri}
        sentUri={sentUri}
        allUri={allUri}
      />
    </div>
  );
}
